import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import DatePicker from "@cloudscape-design/components/date-picker";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Multiselect from "@cloudscape-design/components/multiselect";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import {
	BOOLEAN_OPTIONS,
	COHORT_LOGICAL_OPERATOR_OPTIONS,
	COHORT_MODULE_OPTIONS,
	CRYPTO_OR_FIAT_OPTIONS,
	PARTNER_PAYMENT_TYPE_OPTIONS,
	createCohortId,
	formatConditionValue,
	getCohortFieldOptionsByModule,
	getCohortOperatorOptions,
	getGroupKeyByModule,
	isConditionValueEmpty,
	normalizeCohortConditions,
	normalizeConditionsForSave,
} from "../helpers/cohortConditions";

function CohortConditionsEditor({ conditions, allowedFields, onChange, onPreview, previewCount, previewLoading, previewError }) {
	const allowedFieldSet = React.useMemo(() => (Array.isArray(allowedFields) ? new Set(allowedFields) : null), [allowedFields]);
	const moduleOptions = React.useMemo(
		() => COHORT_MODULE_OPTIONS.filter((module) => !allowedFieldSet || getCohortFieldOptionsByModule(module.value).some((field) => allowedFieldSet.has(field.value))),
		[allowedFieldSet]
	);
	const defaultModule = moduleOptions[0]?.value || "profile";
	const normalized = React.useMemo(() => {
		const parsed = normalizeCohortConditions(conditions);
		if (!allowedFieldSet) return parsed;

		const filteredConditions = parsed.conditions.filter((condition) => allowedFieldSet.has(condition.field));
		const allowedModules = new Set(moduleOptions.map((module) => module.value));
		let filteredGroups = parsed.groups.filter((group) => allowedModules.has(group.module));

		if (!filteredGroups.length) {
			filteredGroups = [
				{
					groupId: createCohortId(),
					module: defaultModule,
					groupKey: getGroupKeyByModule(defaultModule),
					groupLogicalOperator: null,
				},
			];
		}

		return {
			groups: filteredGroups,
			conditions: normalizeConditionsForSave(filteredConditions, filteredGroups),
		};
	}, [conditions, allowedFieldSet, defaultModule, moduleOptions]);
	const [cohortGroups, setCohortGroups] = React.useState(normalized.groups);
	const [newConditionByGroupId, setNewConditionByGroupId] = React.useState({});

	const currentConditions = normalized.conditions;

	const commitChanges = (nextConditions, nextGroups = cohortGroups) => {
		const normalizedConditions = normalizeConditionsForSave(nextConditions, nextGroups);
		onChange(normalizedConditions);
	};

	const updateNewCondition = (groupId, patch) => {
		setNewConditionByGroupId((prev) => ({
			...prev,
			[groupId]: {
				...(prev[groupId] || {}),
				...patch,
			},
		}));
	};

	const resetNewCondition = (groupId) => {
		setNewConditionByGroupId((prev) => ({
			...prev,
			[groupId]: {},
		}));
	};

	const addGroup = () => {
		const nextGroups = [
			...cohortGroups,
			{
				groupId: createCohortId(),
				module: defaultModule,
				groupKey: getGroupKeyByModule(defaultModule),
				groupLogicalOperator: "AND",
			},
		];
		setCohortGroups(nextGroups);
		commitChanges(currentConditions, nextGroups);
	};

	const updateGroup = (groupId, patch) => {
		const nextGroups = cohortGroups.map((group, index) => {
			if (group.groupId !== groupId) return group;
			const module = patch.module || group.module;

			return {
				...group,
				...patch,
				groupKey: getGroupKeyByModule(module),
				groupLogicalOperator: index === 0 ? null : patch.groupLogicalOperator || group.groupLogicalOperator || "AND",
			};
		});

		setCohortGroups(nextGroups);
		commitChanges(currentConditions, nextGroups);
	};

	const changeGroupModule = (groupId, module) => {
		const nextGroups = cohortGroups.map((group) =>
			group.groupId === groupId
				? {
						...group,
						module,
						groupKey: getGroupKeyByModule(module),
				  }
				: group
		);
		const nextConditions = currentConditions.filter((condition) => condition.groupId !== groupId);

		setCohortGroups(nextGroups);
		resetNewCondition(groupId);
		commitChanges(nextConditions, nextGroups);
	};

	const removeGroup = (groupId) => {
		const nextGroups = cohortGroups
			.filter((group) => group.groupId !== groupId)
			.map((group, index) => ({
				...group,
				groupLogicalOperator: index === 0 ? null : group.groupLogicalOperator || "AND",
			}));
		const groups = nextGroups.length ? nextGroups : normalized.groups;
		const nextConditions = currentConditions.filter((condition) => condition.groupId !== groupId);

		setCohortGroups(groups);
		commitChanges(nextConditions, groups);
	};

	const removeCondition = (id) => {
		commitChanges(
			currentConditions.filter((condition) => condition.id !== id),
			cohortGroups
		);
	};

	const addCondition = (group, groupIndex) => {
		const newCondition = newConditionByGroupId[group.groupId] || {};
		if (!newCondition.field || !newCondition.operator || isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value")) {
			value = value.value;
		}
		if (["lang", "bot_id"].includes(newCondition.field) && ["IN", "NOT IN"].includes(newCondition.operator)) {
			value = String(value)
				.split(",")
				.map((item) => item.trim())
				.filter(Boolean);
		}
		if (isConditionValueEmpty(value)) return;

		const groupConditions = currentConditions.filter((condition) => condition.groupId === group.groupId);
		const nextCondition = {
			id: createCohortId(),
			field: newCondition.field,
			fieldLabel: newCondition.fieldLabel,
			section: newCondition.section,
			operator: newCondition.operator,
			value,
			conditionalType: newCondition.conditionalType,
			specialType: newCondition.specialType,
			logicalOperator: groupConditions.length ? newCondition.logicalOperator || "AND" : null,
			conditionOrder: groupConditions.length,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
		};

		commitChanges([...currentConditions, nextCondition], cohortGroups);
		resetNewCondition(group.groupId);
	};

	const renderValueInput = (groupId, condition) => {
		if (condition.field === "date_last_login" && condition.operator === "BETWEEN") {
			const dateRange = Array.isArray(condition.value) ? condition.value : ["", ""];

			return (
				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<DatePicker
						onChange={({ detail }) => updateNewCondition(groupId, { value: [detail.value, dateRange[1] || ""] })}
						value={dateRange[0] || ""}
						placeholder="Start date"
					/>
					<DatePicker
						onChange={({ detail }) => updateNewCondition(groupId, { value: [dateRange[0] || "", detail.value] })}
						value={dateRange[1] || ""}
						placeholder="End date"
					/>
				</Grid>
			);
		}

		if (["lang", "bot_id"].includes(condition.field) && ["IN", "NOT IN"].includes(condition.operator)) {
			return (
				<Input
					placeholder="Enter comma-separated values"
					value={Array.isArray(condition.value) ? condition.value.join(", ") : condition.value || ""}
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.value })}
				/>
			);
		}

		if (condition.conditionalType === "multiselect" && condition.specialType === "riskModel") {
			return (
				<Multiselect
					selectedOptions={getRiskModelSelectedOptions(condition.value)}
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) })}
					options={RISK_MODEL_OPTIONS}
					placeholder="Select risk models"
					selectedAriaLabel="Selected"
				/>
			);
		}

		if (condition.conditionalType === "date") {
			return (
				<DatePicker
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.value })}
					value={Array.isArray(condition.value) ? condition.value[0] || "" : condition.value || ""}
					placeholder="Select a date"
				/>
			);
		}

		if (condition.conditionalType === "select" && condition.specialType === "boolean") {
			return (
				<Select
					selectedOption={BOOLEAN_OPTIONS.find((option) => option.value === condition.value) || null}
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.selectedOption.value })}
					options={BOOLEAN_OPTIONS}
					placeholder="Select value"
				/>
			);
		}

		if (condition.conditionalType === "select" && condition.specialType === "cryptoOrFiat") {
			return (
				<Select
					selectedOption={CRYPTO_OR_FIAT_OPTIONS.find((option) => option.value === condition.value) || null}
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.selectedOption.value })}
					options={CRYPTO_OR_FIAT_OPTIONS}
					placeholder="Select type"
				/>
			);
		}

		if (condition.conditionalType === "select" && condition.specialType === "partnerPaymentType") {
			return (
				<Select
					selectedOption={PARTNER_PAYMENT_TYPE_OPTIONS.find((option) => option.value === condition.value) || null}
					onChange={({ detail }) => updateNewCondition(groupId, { value: detail.selectedOption.value })}
					options={PARTNER_PAYMENT_TYPE_OPTIONS}
					placeholder="Select payment type"
				/>
			);
		}

		return (
			<Input
				placeholder="Enter value"
				type={condition.conditionalType === "count" ? "number" : "text"}
				value={condition.value || ""}
				onChange={({ detail }) => updateNewCondition(groupId, { value: detail.value })}
			/>
		);
	};

	return (
		<SpaceBetween size="m">
			<Container
				header={
					<Header
						variant="h3"
						description="Build the target audience. Conditions inside a group and groups between each other can use AND/OR."
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								{typeof previewCount === "number" ? <Badge color="blue">{previewCount} users</Badge> : null}
								{typeof onPreview === "function" ? (
									<Button loading={previewLoading} onClick={() => onPreview(normalizeConditionsForSave(currentConditions, cohortGroups))}>
										View audience
									</Button>
								) : null}
								<Button variant="primary" iconName="add-plus" onClick={addGroup}>
									Add group
								</Button>
							</SpaceBetween>
						}
					>
						Cohort conditions
					</Header>
				}
			>
				<SpaceBetween size="m">
					{previewError ? <Alert type="error">{previewError}</Alert> : null}
					{cohortGroups.map((group, groupIndex) => {
						const groupConditions = currentConditions.filter((condition) => condition.groupId === group.groupId);
						const selectedModule = moduleOptions.find((option) => option.value === group.module) || moduleOptions[0];
						const logicalOperator = COHORT_LOGICAL_OPERATOR_OPTIONS.find((option) => option.value === (group.groupLogicalOperator || "AND")) || COHORT_LOGICAL_OPERATOR_OPTIONS[0];
						const newCondition = newConditionByGroupId[group.groupId] || {};
						const fieldOptions = getCohortFieldOptionsByModule(group.module).filter((field) => !allowedFieldSet || allowedFieldSet.has(field.value));
						const selectedField = fieldOptions.find((option) => option.value === newCondition.field) || null;
						const operatorOptions = getCohortOperatorOptions(selectedField);
						const selectedOperator = operatorOptions.find((option) => option.value === newCondition.operator) || null;
						const selectedConditionLogicalOperator = COHORT_LOGICAL_OPERATOR_OPTIONS.find((option) => option.value === (newCondition.logicalOperator || "AND")) || COHORT_LOGICAL_OPERATOR_OPTIONS[0];

						return (
							<Container
								key={group.groupId}
								header={
									<Header
										variant="h3"
										actions={
											<SpaceBetween direction="horizontal" size="xs">
												{groupIndex > 0 ? (
													<Select
														selectedOption={logicalOperator}
														options={COHORT_LOGICAL_OPERATOR_OPTIONS}
														onChange={({ detail }) => updateGroup(group.groupId, { groupLogicalOperator: detail.selectedOption.value })}
													/>
												) : null}
												<Select
													selectedOption={selectedModule}
													options={moduleOptions}
													onChange={({ detail }) => changeGroupModule(group.groupId, detail.selectedOption.value)}
												/>
												<Button disabled={cohortGroups.length === 1} onClick={() => removeGroup(group.groupId)}>
													Remove group
												</Button>
											</SpaceBetween>
										}
									>
										Group {groupIndex + 1}
									</Header>
								}
							>
								<Table
									trackBy="id"
									items={groupConditions}
									columnDefinitions={[
										{ id: "logicalOperator", header: "Logic", cell: (item) => item.logicalOperator || "-" },
										{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
										{ id: "operator", header: "Operator", cell: (item) => item.operator },
										{ id: "value", header: "Value", cell: (item) => formatConditionValue(item.value) },
										{
											id: "actions",
											header: "Control",
											cell: (item) => (
												<Button variant="icon" iconName="delete-marker" onClick={() => removeCondition(item.id)}>
													Remove
												</Button>
											),
										},
									]}
									empty={<Box textAlign="center">No conditions in this group</Box>}
									footer={
										<FormField label="Add condition" stretch>
											<Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }, { colspan: 2 }]}>
												<Select
													disabled={!groupConditions.length}
													selectedOption={selectedConditionLogicalOperator}
													options={COHORT_LOGICAL_OPERATOR_OPTIONS}
													onChange={({ detail }) => updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value })}
												/>
												<Select
													selectedOption={selectedField}
													placeholder="Select field"
													options={fieldOptions}
													onChange={({ detail }) =>
														updateNewCondition(group.groupId, {
															field: detail.selectedOption.value,
															fieldLabel: detail.selectedOption.label,
															conditionalType: detail.selectedOption.type,
															specialType: detail.selectedOption.specialType,
															section: detail.selectedOption.section,
															value: detail.selectedOption.type === "multiselect" ? [] : "",
															operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : "",
														})
													}
												/>
												<Select
													selectedOption={selectedOperator}
													placeholder="Operator"
													disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
													options={operatorOptions}
													onChange={({ detail }) => {
														const operator = detail.selectedOption.value;
														let value = newCondition.value;

														if (newCondition.field === "date_last_login" && operator === "BETWEEN") {
															value = Array.isArray(value) ? value : [value || "", ""];
														} else if (Array.isArray(value)) {
															value = value[0] || "";
														}

														updateNewCondition(group.groupId, { operator, value });
													}}
												/>
												{renderValueInput(group.groupId, newCondition)}
												<Button disabled={!newCondition.field || !newCondition.operator || isConditionValueEmpty(newCondition.value)} onClick={() => addCondition(group, groupIndex)}>
													Add
												</Button>
											</Grid>
										</FormField>
									}
								/>
							</Container>
						);
					})}
				</SpaceBetween>
			</Container>
		</SpaceBetween>
	);
}

export default CohortConditionsEditor;
